import { getThemeLayoutClass } from '../../theme/crystalLayout';
import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import * as motion from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { 
    ChevronRight, Wrench, Smartphone, ArrowRight, ShieldCheck, Zap, 
    ShoppingBag, Star, Sparkles, 
    Battery, Camera, Volume2, ShieldAlert, ArrowUpRight, X, VolumeX,
    MapPin, Phone, Mail, Clock, ExternalLink
} from 'lucide-react';
import { Brand, Page, Part } from '../../types';
import { AppContext } from '../../context/AppContext';
import BrandLogo from '../BrandLogo';
import GlobalModelSearch from '../GlobalModelSearch';
import ModelImage from '../ModelImage';
import { TopicClusters } from '../SemanticLinking';
import publicClaimContract from '../../../shared/publicClaimContract.json';
import { useDeferredHeroVideo } from '../../hooks/useDeferredHeroVideo';
import { publicImageThumbnail, reviewAvatarImage } from '../../utils/responsiveImage';

interface CrystalThemeProps {
    repairData: Brand[];
    handleSelectBrand: (brandId: string) => void;
    setPage: (page: Page) => void;
}

interface CarouselProduct {
    id: string | number;
    name: string;
    category: string;
    price: number;
    originalPrice: number;
    image: string;
    badge: string;
    reviewsCount: number;
    colorName: string;
    dbItem?: Part;
}

interface PublicReview {
    id?: string | number;
    customer_name: string;
    rating: number;
    comment: string;
    relative_time_description?: string;
    is_verified?: number;
    source?: string;
    author_photo_url?: string;
}

// Pre-configured list of premium accessories that users can interact with and purchase
const BRANDED_ACCESSORIES = [
    {
        id: 'acc-glass-ultra',
        name: 'Verre Trempé Oléophobe 9H Ultra-Résistant',
        category: 'Accessoire',
        price: 29.90,
        originalPrice: 39.90,
        rating: 4.9,
        reviewsCount: 142,
        image: 'https://images.unsplash.com/photo-1608156639585-b3a032ef9689?q=80&w=400',
        badge: 'Meilleure Vente',
        colorName: 'Transparent HD'
    },
    {
        id: 'acc-case-magsafe',
        name: 'Coque Hybride Magnétique MagSafe & Antichoc',
        category: 'Accessoire',
        price: 49.90,
        originalPrice: 59.90,
        rating: 4.8,
        reviewsCount: 96,
        image: 'https://images.unsplash.com/photo-1541807084-5c52b6b3adef?q=80&w=400',
        badge: 'Premium Apple',
        colorName: 'Bleu Abysse'
    },
    {
        id: 'acc-charger-gan',
        name: 'Chargeur Rapide GaN 65W Multi-Ports (PD/QC)',
        category: 'Accessoire',
        price: 39.90,
        originalPrice: 49.90,
        rating: 4.9,
        reviewsCount: 210,
        image: 'https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?q=80&w=400',
        badge: 'Nouvel Arrivage',
        colorName: 'Élastomère Blanc'
    }
];

const CrystalTheme: React.FC<CrystalThemeProps> = ({ repairData, handleSelectBrand, setPage }) => {
    const context = useContext(AppContext);
    
    // Diagnostic State
    const [selectedDiagProblem, setSelectedDiagProblem] = useState<string | null>(null);
    const [activeAccessoryIdx, setActiveAccessoryIdx] = useState(0);
    const [isModalOpen, setIsMOpen] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const modalVideoRef = useRef<HTMLVideoElement>(null);

    // Helper to format video URL to support both relative and absolute paths cleanly
    const formatVideoUrl = (url: string | undefined): string => {
        if (!url) return '';
        let trimmed = url.trim();
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
            return trimmed;
        }
        if (trimmed.startsWith('./')) {
            trimmed = trimmed.substring(2);
        }
        if (trimmed.startsWith('/')) {
            return trimmed;
        }
        return `/${trimmed}`;
    };

    const getCityFromAddress = (address?: string): string => {
        if (!address) return '';
        const cleaned = address.replace(/\n/g, ' ').trim();
        const zipMatch = cleaned.match(/\b\d{4}\s+([^,\d\n]+)/);
        if (zipMatch && zipMatch[1]) {
            const val = zipMatch[1].trim();
            // clean potential trailing address notes
            return val.split(/\s+-\s+/)[0].split(',')[0].trim();
        }
        const lastSegment = cleaned.split(',').map(segment => segment.trim()).filter(Boolean).at(-1) || '';
        return lastSegment.replace(/^\d{4}\s+/u, '').trim();
    };

    // Filtered Video for showcasing - scan all slides for video Url to guarantee it plays
    const activeVideoSlide = context?.heroSlides?.find(slide => slide.videoUrl && slide.videoUrl.trim() !== '');
    const customVideo = activeVideoSlide?.videoUrl;
    const activeVideoUrl = formatVideoUrl(customVideo);
    
    // Fallback poster image when video doesn't play or is loading
    const slideImage = activeVideoSlide?.imageUrl || context?.heroSlides?.find(slide => slide.imageUrl && slide.imageUrl.trim() !== '')?.imageUrl;
    const fallbackPoster = slideImage || '';
    const responsiveHeroPoster = publicImageThumbnail(fallbackPoster, 360);

    const hasCustomVideo = !!(activeVideoUrl && activeVideoUrl.trim() !== '');
    const shouldLoadHeroVideo = useDeferredHeroVideo(hasCustomVideo);
    const [isShowcaseVideoReady, setIsShowcaseVideoReady] = useState(false);

    const showcaseVideoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        setIsShowcaseVideoReady(false);
        if (!hasCustomVideo || !shouldLoadHeroVideo) return;
        const videoEl = showcaseVideoRef.current;
        if (!videoEl) return;

        // Force maximum compatibility properties
        videoEl.muted = true;
        videoEl.defaultMuted = true;
        videoEl.autoplay = true;
        videoEl.setAttribute('muted', 'true');
        videoEl.setAttribute('playsinline', 'true');
        videoEl.setAttribute('webkit-playsinline', 'true');
        videoEl.setAttribute('autoplay', 'true');
        videoEl.setAttribute('preload', 'metadata');

        // Play helper that catches and falls back to user gesture
        const forcePlay = () => {
            const playPromise = videoEl.play();
            if (playPromise !== undefined) {
                playPromise.catch(() => {
                    const triggerOnUserGesture = () => {
                        videoEl.play().catch(e => console.log("[MOBILBO] Play failed after gesture:", e));
                        window.removeEventListener('click', triggerOnUserGesture);
                        window.removeEventListener('touchstart', triggerOnUserGesture);
                        window.removeEventListener('scroll', triggerOnUserGesture);
                    };
                    window.addEventListener('click', triggerOnUserGesture);
                    window.addEventListener('touchstart', triggerOnUserGesture);
                    window.addEventListener('scroll', triggerOnUserGesture);
                });
            }
        };

        // Modern IntersectionObserver to play/pause based on screen visibility
        let observer: IntersectionObserver | null = null;
        if ('IntersectionObserver' in window) {
            observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        forcePlay();
                    } else {
                        videoEl.pause();
                    }
                });
            }, { threshold: 0.1 });
            observer.observe(videoEl);
        } else {
            // Fallback for older browsers
            forcePlay();
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, [activeVideoUrl, hasCustomVideo, shouldLoadHeroVideo]);

    // Programmatically handle and force play the modal video when opened
    useEffect(() => {
        if (!isModalOpen) return;
        const timer = setTimeout(() => {
            const videoEl = modalVideoRef.current;
            if (!videoEl) return;

            videoEl.muted = isMuted;
            videoEl.defaultMuted = isMuted;
            videoEl.setAttribute('playsinline', 'true');
            videoEl.setAttribute('webkit-playsinline', 'true');
            if (isMuted) {
                videoEl.setAttribute('muted', 'true');
            } else {
                videoEl.removeAttribute('muted');
            }

            const playPromise = videoEl.play();
            if (playPromise !== undefined) {
                playPromise.catch(err => {
                    console.log("[MOBILBO] Modal autoplay blocked, waiting for user interaction:", err);
                });
            }
        }, 150); // slight delay to allow modal entrance animation to complete smoothly

        return () => clearTimeout(timer);
    }, [isModalOpen, isMuted]);

    const [fetchedReviews, setFetchedReviews] = useState<PublicReview[]>([]);

    useEffect(() => {
        if (!shouldLoadHeroVideo) return;
        const controller = new AbortController();
        const loadReviews = async () => {
            try {
                const response = await fetch('/api/public/reviews', { signal: controller.signal, credentials: 'same-origin' });
                if (!response.ok) throw new Error(`REVIEWS_HTTP_${response.status}`);
                const reviews = await response.json();
                if (Array.isArray(reviews)) setFetchedReviews(reviews);
            } catch (err) {
                if (!controller.signal.aborted) console.error("Error loading public reviews:", err);
            }
        };
        void loadReviews();
        return () => controller.abort();
    }, [shouldLoadHeroVideo]);

    const defaultFallbackReviews = useMemo<PublicReview[]>(() => [
        {
            customer_name: "Sébastien M.",
            rating: 5,
            comment: "Service incroyable ! Batterie d'iPhone remplacée sous mes yeux en 15 minutes chrono. Technicien hyper professionnel et prix très correct. Un grand bravo à l'équipe !",
            relative_time_description: "Il y a 3 jours",
            is_verified: 1,
            source: "Google",
            author_photo_url: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=150"
        },
        {
            customer_name: "Laetitia G.",
            rating: 5,
            comment: "Je suis venue pour un problème de connecteur de charge. L'accueil était clair et le diagnostic m'a été expliqué.",
            relative_time_description: "Il y a une semaine",
            is_verified: 1,
            source: "Google",
            author_photo_url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=150"
        },
        {
            customer_name: "Julien R.",
            rating: 5,
            comment: "Boutique très propre, accueil chaleureux et grand choix d'accessoires de qualité. La pose du verre trempé est parfaite. Je recommande sans hésitation !",
            relative_time_description: "Il y a 2 semaines",
            is_verified: 1,
            source: "Google",
            author_photo_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=150"
        }
    ], []);

    const fallbackAvatars = useMemo(() => [
        "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=150",
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=150",
        "https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=150",
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=150",
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=150"
    ], []);

    const mergedReviews = useMemo<PublicReview[]>(() => {
        const list = Array.isArray(fetchedReviews) ? [...fetchedReviews] : [];
        if (Array.isArray(defaultFallbackReviews)) {
            defaultFallbackReviews.forEach(fallback => {
                if (!fallback) return;
                const fbName = (fallback.customer_name || '').toLowerCase().trim();
                if (fbName) {
                    const exists = list.some(r => {
                        const rName = (r?.customer_name || '').toLowerCase().trim();
                        return rName === fbName;
                    });
                    if (!exists) {
                        list.push(fallback);
                    }
                }
            });
        }
        const avatars = Array.isArray(fallbackAvatars) ? fallbackAvatars : [];
        const mapped = list.map((review, index) => ({
            ...review,
            customer_name: review?.customer_name || "Client",
            rating: parseInt(String(review?.rating)) || 5,
            comment: review?.comment || "",
            author_photo_url: review?.author_photo_url || (avatars.length > 0 ? avatars[index % avatars.length] : undefined),
            relative_time_description: review?.relative_time_description || "Il y a quelques jours",
            is_verified: review?.is_verified !== undefined ? Number(review.is_verified) : 1,
            source: review?.source || "Google"
        })).filter(r => r.comment && r.comment.trim() !== '');

        if (mapped.length === 0) {
            return defaultFallbackReviews;
        }
        return mapped;
    }, [fetchedReviews, defaultFallbackReviews, fallbackAvatars]);

    // Simulated list of phone issues for emotional interactive diagnosis banner
    const phoneIssuesList = [
        { id: 'screen', label: 'Écran Fissuré ou Tactile défaillant', icon: Smartphone, color: 'text-blue-600', desc: 'Option de remplacement confirmée après identification de l\'appareil.' },
        { id: 'battery', label: 'Batterie faible ou ne charge plus', icon: Battery, color: 'text-amber-500', desc: 'Remplacement proposé selon le diagnostic et la pièce disponible.' },
        { id: 'camera', label: 'Objectif Caméra rayé ou flou', icon: Camera, color: 'text-purple-500', desc: publicClaimContract.home.cameraRepairDescription },
        { id: 'sound', label: 'Haut-parleurs / Microphone grésillant', icon: Volume2, color: 'text-indigo-500', desc: 'Nettoyage professionnel ultrason ou changement de nappe.' },
        { id: 'general', label: 'Boutons bloqués ou Châssis tordu', icon: ShieldAlert, color: 'text-rose-500', desc: 'Ajustement d\'ingénierie fine sur l\'armature en alliage.' }
    ];

    // Direct Actions for accessories & phones
    const allDbParts = useMemo(() => context?.parts || [], [context?.parts]);
    const dbAccessories = useMemo(() => allDbParts.filter(p => p.catalogKind === 'ACCESSORY' && !p.isArchived), [allDbParts]);
    const dbPhones = useMemo(() => allDbParts.filter(p => p.catalogKind === 'PHONE' && !p.isArchived), [allDbParts]);
    const availableDynamic = useMemo(() => [...dbAccessories, ...dbPhones], [dbAccessories, dbPhones]);

    // Deterministic dynamic daily rotation selector
    const getDailyProducts = (productList: Part[]): Part[] => {
        if (productList.length === 0) return [];
        const day = new Date().getDate(); // 1 to 31
        const shuffled = [...productList];
        const startIdx = (day * 3) % productList.length;
        const result = [];
        for (let i = 0; i < Math.min(5, productList.length); i++) {
            result.push(shuffled[(startIdx + i) % productList.length]);
        }
        return result;
    };

    // Mapping helper to standard display properties
    const mapDbProductToCarousel = (p: Part): CarouselProduct => {
        return {
            id: p.id,
            name: p.name,
            category: p.mainCategory || 'Accessoire',
            price: Number(p.price) || 0,
            originalPrice: (Number(p.price) * 1.25) || 0,
            image: p.image || '',
            badge: p.stock > 0 ? 'En Stock' : 'Sur Demande',
            reviewsCount: (Number(p.id || 0) % 17) + 12,
            colorName: p.color || p.quality || 'Gris Sidéral',
            dbItem: p
        };
    };

    // Calculate active items
    const showcaseProducts = context?.showcaseProducts;
    const activeProductsList = useMemo<CarouselProduct[]>(() => {
        // 1. If manual pinned showcase is stored in context
        const contextShowcase = showcaseProducts;
        if (contextShowcase && contextShowcase.length > 0) {
            const pinned = allDbParts.filter(p => contextShowcase.includes(String(p.id)));
            if (pinned.length > 0) return pinned.map(mapDbProductToCarousel);
        }
        
        // 2. Daily dynamic catalog rotation (fully automated)
        if (availableDynamic.length > 0) {
            const daily = getDailyProducts(availableDynamic);
            return daily.map(mapDbProductToCarousel);
        }
        
        // 3. Fallback to hardcoded elegant assets
        return BRANDED_ACCESSORIES;
    }, [allDbParts, availableDynamic, showcaseProducts]);

    // Ensure state activeAccessoryIdx doesn't exceed length of activeProductsList
    useEffect(() => {
        if (activeAccessoryIdx >= activeProductsList.length) {
            setActiveAccessoryIdx(0);
        }
    }, [activeProductsList, activeAccessoryIdx]);

    const handleProductClick = (item: CarouselProduct) => {
        if (!context) return;
        if (item.dbItem) {
            const actualItem = item.dbItem;
            if (actualItem.catalogKind === 'PHONE') {
                if (context.setSelectedPhoneForPurchase) {
                    context.setSelectedPhoneForPurchase(actualItem);
                    setPage(Page.ProductDetail);
                }
            } else {
                if (context.setSelectedPartForPurchase) {
                    context.setSelectedPartForPurchase(actualItem);
                    setPage(Page.AccessoryDetail);
                }
            }
        } else {
            setPage(Page.Accessories);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleAddAccessoryToCart = (item: CarouselProduct) => {
        if (!context) return;
        
        if (item.dbItem) {
            context.addToCart(item.dbItem, 1, item.dbItem.catalogKind === 'PHONE' ? 'Phone' : 'Part');
            context.setIsCartOpen(true);
            context.notify(`🛒 ${item.name} ajouté au panier avec succès !`, 'success');
        } else {
            const mockPart: Part = {
                id: item.id,
                name: item.name,
                price: item.price,
                image: item.image,
                mainCategory: 'Accessoires',
                productType: 'Accessoire',
                catalogKind: 'ACCESSORY',
                stock: 12,
                sku: `fallback-${item.id}`,
                brand: '',
                quality: '',
                description: ''
            };
            context.addToCart(mockPart, 1, 'Part');
            context.setIsCartOpen(true);
            context.notify(`🛒 ${item.name} ajouté au panier avec succès !`, "success");
        }
    };

    return (
        <div className={getThemeLayoutClass(context?.activeThemeConfig, 'crystalPage')}>
            
            {/* Ambient Background Grid and Animated Neon Orbs */}
            <div className="absolute inset-0 bg-[#eef3fa] bg-[radial-gradient(#bed3f2_1.2px,transparent_1.2px)] [background-size:24px_24px] pointer-events-none" />
            
            <motion.div 
                animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
                className="absolute w-[500px] h-[500px] bg-sky-300/20 rounded-full blur-[140px] -top-40 -left-60 pointer-events-none"
            />
            <motion.div 
                animate={{ scale: [1, 1.15, 1], rotate: [0, -120, 0] }}
                transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
                className="absolute w-[600px] h-[600px] bg-blue-400/15 rounded-full blur-[160px] top-1/3 -right-80 pointer-events-none"
            />
            
            {/* -------------------------------------------------------------
                SECTION: IMMERSIVE ADVANCED HERO
               ------------------------------------------------------------- */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 pt-32 xs:pt-36 sm:pt-40 md:pt-24 pb-16 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    
                    {/* Left side: Premium Catchphrase and Brand Values */}
                    <div className="lg:col-span-7 space-y-6 text-left">
                        <motion.h1 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-black text-slate-950 tracking-tight leading-[1.2] lg:leading-none uppercase"
                        >
                            Une Précision <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-sky-500 to-blue-700">Céleste</span>, <br />
                            {publicClaimContract.home.heroLine}
                        </motion.h1>

                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-slate-600 text-sm sm:text-base font-medium max-w-2xl leading-relaxed"
                        >
                            Chaque intervention commence par l'identification de l'appareil et du service adapté. Consultez les réparations et protections disponibles, puis demandez une confirmation selon votre modèle.
                        </motion.p>

                        {/* Search & Dynamic Suggesting Tool */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="pt-4 max-w-lg"
                        >
                            <div className="bg-white p-2 rounded-[2rem] border border-blue-50 shadow-[0_20px_45px_rgba(59,130,246,0.1)]">
                                <GlobalModelSearch />
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2 items-center pl-4">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Populaire :</span>
                                {repairData.slice(0, 3).map(b => (
                                    <button
                                        key={`crystal-hero-brand-${b.id}`}
                                        onClick={() => handleSelectBrand(b.id)}
                                        className="text-[10px] font-black uppercase text-blue-600 hover:text-blue-800 tracking-wider bg-blue-50 hover:bg-blue-100/70 px-3 py-1 rounded-full transition-all"
                                    >
                                        {b.name} →
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Right side: Interacting Glass Video & Floating Orbs */}
                    <div className="lg:col-span-5 relative flex justify-center">
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            onClick={() => setIsMOpen(true)}
                            className="relative w-full aspect-[4/5] xs:aspect-[4/5] sm:aspect-square md:aspect-square max-w-[280px] sm:max-w-[340px] md:max-w-[380px] mx-auto rounded-[3rem] xs:rounded-[3.5rem] overflow-hidden bg-white/40 border border-white/60 p-3 sm:p-4 shadow-[0_45px_90px_rgba(59,130,246,0.12)] backdrop-blur-md flex flex-col justify-end group cursor-pointer"
                            style={{
                                backgroundImage: `url(${responsiveHeroPoster})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: '#020617'
                            }}
                        >
                            {responsiveHeroPoster && (
                                <img
                                    src={responsiveHeroPoster}
                                    alt=""
                                    aria-hidden="true"
                                    width={360}
                                    height={640}
                                    loading="eager"
                                    fetchPriority="high"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-cover rounded-[2.5rem] xs:rounded-[2.9rem]"
                                />
                            )}
                            {/* Compliance layout video or beautiful abstract live loops - only rendered if custom video is present */}
                            {hasCustomVideo && (
                                <video 
                                    ref={showcaseVideoRef}
                                    src={shouldLoadHeroVideo ? activeVideoUrl : undefined}
                                    className="absolute inset-0 w-full h-full object-cover rounded-[2.5rem] xs:rounded-[2.9rem] scale-100 group-hover:scale-105 transition-transform duration-[4s]" 
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    webkit-playsinline="true"
                                    preload="none"
                                    poster={responsiveHeroPoster}
                                    onLoadedData={() => setIsShowcaseVideoReady(true)}
                                    onPlaying={() => setIsShowcaseVideoReady(true)}
                                    onError={() => setIsShowcaseVideoReady(false)}
                                    style={{ visibility: isShowcaseVideoReady ? 'visible' : 'hidden' }}
                                />
                            )}

                            <div className="absolute inset-x-4 top-4 flex justify-between items-center z-10 z-[12]">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md border border-blue-100 text-[9px] font-black uppercase text-blue-600 tracking-widest shadow-lg shadow-blue-500/10">
                                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-ping" />
                                    {(context?.footerInfo?.siteName || context?.footerInfo?.nom_du_magasin || 'Mobilbo')} Showcase
                                </span>
                            </div>

                            <div className="relative z-10 bg-white/95 backdrop-blur-xl p-4 sm:p-6 rounded-2xl xs:rounded-[2.2rem] border border-blue-50/50 shadow-2xl flex flex-col gap-1.5 sm:gap-2">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-xs sm:text-sm font-black text-slate-900 uppercase tracking-tight">Atelier de Restauration</h2>
                                    <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 group-hover:scale-125 transition-transform" />
                                </div>
                                <p className="text-[10px] sm:text-[11px] text-slate-500 font-medium leading-relaxed">
                                    Les interventions, y compris la micro-soudure, sont prises en charge par notre équipe de {getCityFromAddress(context?.footerInfo?.adresse)} selon le diagnostic.
                                </p>
                            </div>
                        </motion.div>

                        {/* Hover floats representing microchips / batteries */}
                        <motion.div 
                            animate={{ y: [-10, 10, -10] }}
                            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -right-6 top-12 w-16 h-16 bg-white rounded-3xl border border-blue-100 shadow-xl flex items-center justify-center pointer-events-none hidden sm:flex"
                        >
                            <Battery className="w-8 h-8 text-blue-500 animate-pulse" />
                        </motion.div>
                        <motion.div 
                            animate={{ y: [10, -10, 10] }}
                            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            className="absolute -left-10 bottom-12 w-14 h-14 bg-white rounded-3xl border border-blue-100 shadow-xl flex items-center justify-center pointer-events-none hidden sm:flex"
                        >
                            <Wrench className="w-6 h-6 text-sky-500" />
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* -------------------------------------------------------------
                SECTION: THE DUAL CRITICAL CUSTOMER PATH
                (Guides User through emotional, interactive diagnostics 
                 OR Premium Accessory curation)
               ------------------------------------------------------------- */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 relative z-10">
                <div className="bg-white p-12 rounded-[3rem] border border-slate-100 shadow-xl">
                    <h2 className="text-3xl font-black text-slate-950 uppercase tracking-tight mb-8">Votre Expert Réparation à votre région</h2>
                    <p className="text-slate-600 font-medium leading-relaxed mb-6">
                        Mobilbo propose des services de réparation de smartphones, tablettes et ordinateurs en Valais. Les composants et conditions applicables sont précisés avec la prestation.
                    </p>
                    <p className="text-slate-600 font-medium leading-relaxed">
                        Que vous ayez un écran cassé, une batterie défectueuse ou un problème de logiciel, nous proposons des solutions rapides, fiables et abordables. Nous intervenons sur toutes les grandes marques : Apple iPhone, Samsung Galaxy, Huawei, et bien d'autres.
                    </p>
                </div>
            </section>
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
                    <span className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-600">Explorez Notre Savoir-Faire</span>
                    <h2 className="text-3xl md:text-4xl font-black text-slate-950 uppercase tracking-tight leading-none">
                        Que Recherchez-vous Aujourd'hui ?
                    </h2>
                    <p className="text-slate-500 font-medium text-xs md:text-sm">
                        Sélectionnez une option ci-dessous pour décrire votre besoin et consulter le parcours correspondant.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    
                    {/* LEFT PANEL: INTERACTIVE DIAGNOSTIC PATH (EMOTIONAL & HELPFUL) */}
                    <div className="bg-gradient-to-br from-white via-white to-blue-50/20 p-8 rounded-[3rem] border border-white shadow-[0_30px_60px_rgba(59,130,246,0.04)] flex flex-col justify-between">
                        <div>
                            <div className="flex gap-4 items-center mb-6">
                                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center shadow-md">
                                    <Wrench className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <span className="text-[9px] font-black uppercase text-blue-600 tracking-widest block">Simulation Directe</span>
                                    <h3 className="text-xl font-black text-slate-900 uppercase">1. Mon Téléphone Est En Panne</h3>
                                </div>
                            </div>
                            
                            <p className="text-slate-500 font-medium text-xs mb-6Leading-relaxed">
                                Un bug, un écran brisé ou une batterie fatiguée ? Sélectionnez le symptôme ci-dessous pour consulter le parcours de réparation.
                            </p>

                            <div className="space-y-3">
                                {phoneIssuesList.map(issue => {
                                    const IsSelected = selectedDiagProblem === issue.id;
                                    const IconComponent = issue.icon;
                                    return (
                                        <button
                                            key={`crystal-diag-issue-${issue.id}`}
                                            onClick={() => setSelectedDiagProblem(IsSelected ? null : issue.id)}
                                            className={`w-full flex items-center justify-between p-4 rounded-2xl border text-left transition-all duration-300 ${
                                                IsSelected 
                                                    ? 'bg-blue-600 border-blue-600 shadow-[0_10px_20px_rgba(37,99,235,0.2)] text-white' 
                                                    : 'bg-slate-50 border-slate-100 hover:border-blue-200 text-slate-800 hover:bg-white'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${IsSelected ? 'bg-white/20 text-white' : 'bg-white text-slate-700 shadow-sm border border-slate-100'}`}>
                                                    <IconComponent className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <span className="text-xs font-black tracking-tight">{issue.label}</span>
                                                    {IsSelected && (
                                                        <motion.p 
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: 'auto' }}
                                                            className="text-[10px] text-blue-100 mt-1 font-medium leading-normal"
                                                        >
                                                            {issue.desc}
                                                        </motion.p>
                                                    )}
                                                </div>
                                            </div>
                                            <ChevronRight className={`w-4 h-4 transition-transform ${IsSelected ? 'rotate-90 text-white' : 'text-slate-400'}`} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Interactive Diagnostic CTA box */}
                        <div className="mt-8 pt-6 border-t border-slate-100">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Choisir la marque pour réparer :</h4>
                            <div className="grid grid-cols-3 gap-2">
                                {repairData.slice(0, 3).map(brand => (
                                    <button
                                        key={`crystal-diag-brand-${brand.id}`}
                                        onClick={() => handleSelectBrand(brand.id)}
                                        className="py-3 px-2 bg-white hover:bg-blue-600 text-slate-900 hover:text-white border border-slate-100 hover:border-blue-500 rounded-xl font-black text-[10px] uppercase tracking-wider text-center transition-all shadow-sm hover:shadow-md"
                                    >
                                        {brand.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT PANEL: PREMIUM ACCESSORIES (HELPS TO CHOOSE AND SELLS EMOTIONALLY) */}
                    <div className="bg-[#FFFFFF] p-8 rounded-[3rem] border border-blue-50 shadow-[0_35px_70px_rgba(59,130,246,0.06)] flex flex-col justify-between">
                        <div>
                            <div className="flex gap-4 items-center mb-6">
                                <div className="w-12 h-12 bg-sky-100 rounded-2xl flex items-center justify-center shadow-md">
                                    <ShoppingBag className="w-6 h-6 text-sky-600" />
                                </div>
                                <div>
                                    <span className="text-[9px] font-black uppercase text-sky-700 tracking-widest block">Boutique Exclusive</span>
                                    <h3 className="text-xl font-black text-slate-900 uppercase">2. Je Cherche Un Accessoire</h3>
                                </div>
                            </div>

                            <p className="text-slate-500 font-medium text-xs mb-6 leading-relaxed">
                                Un téléphone bien protégé est une sérénité assurée. Parcourez nos meilleurs accessoires et ajoutez-les au panier d'un seul clic avec livraison gratuite dès 50 CHF.
                            </p>

                            {/* Accessory Carousel Showcase */}
                            <div className="relative rounded-2xl border border-slate-100 bg-slate-50/50 p-4 mb-4">
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                                    <div 
                                        className="md:col-span-5 relative h-36 rounded-xl overflow-hidden shadow-inner bg-white border border-slate-100 cursor-pointer group flex items-center justify-center p-2"
                                        onClick={() => handleProductClick(activeProductsList[activeAccessoryIdx])}
                                    >
                                        <ModelImage 
                                            id={String(activeProductsList[activeAccessoryIdx]?.id)}
                                            type={activeProductsList[activeAccessoryIdx]?.dbItem?.catalogKind === 'PHONE' ? 'product' : 'part'}
                                            src={activeProductsList[activeAccessoryIdx]?.image}
                                            alt={activeProductsList[activeAccessoryIdx]?.name}
                                            className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700"
                                            noPadding={true}
                                            optimizeWidth={320}
                                        />
                                        <div className="absolute top-2 left-2 bg-blue-600 text-white font-black text-[7px] uppercase tracking-widest px-2 py-1 rounded-md shadow-sm z-20">
                                            {activeProductsList[activeAccessoryIdx]?.badge}
                                        </div>
                                    </div>
                                    
                                    <div className="md:col-span-7 flex flex-col gap-2">
                                        <div className="flex gap-1 items-center">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" />
                                            ))}
                                            <span className="text-[9px] text-slate-600 font-bold ml-1">{activeProductsList[activeAccessoryIdx]?.reviewsCount} avis</span>
                                        </div>
                                        <h4 
                                            className="text-xs font-black text-slate-900 leading-tight uppercase cursor-pointer hover:text-blue-600 transition-colors"
                                            onClick={() => handleProductClick(activeProductsList[activeAccessoryIdx])}
                                        >
                                            {activeProductsList[activeAccessoryIdx]?.name}
                                        </h4>
                                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                                            Teinte : {activeProductsList[activeAccessoryIdx]?.colorName}
                                        </div>
                                        
                                        <div className="flex justify-between items-center mt-2">
                                            <div className="flex items-baseline gap-1.5">
                                                <span className="text-sm font-black text-blue-600">{activeProductsList[activeAccessoryIdx]?.price?.toFixed(2)} CHF</span>
                                                {activeProductsList[activeAccessoryIdx]?.originalPrice > 0 && (
                                                    <span className="text-[10px] text-slate-600 line-through font-medium">{activeProductsList[activeAccessoryIdx]?.originalPrice?.toFixed(2)} CHF</span>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleAddAccessoryToCart(activeProductsList[activeAccessoryIdx])}
                                                className="bg-blue-600 hover:bg-blue-500 text-white font-black text-[9px] uppercase tracking-widest py-2 px-3.5 rounded-xl shadow-[0_4px_12px_rgba(37,99,235,0.2)] hover:shadow-[0_4px_12px_rgba(37,99,235,0.4)] active:scale-95 transition-all"
                                            >
                                                Ajouter +
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Carousel Navigation buttons */}
                            <div className="flex gap-2 justify-end mb-6">
                                {activeProductsList.map((acc, index) => (
                                    <button
                                        key={`crystal-acc-${acc.id || index}-${index}`}
                                        onClick={() => setActiveAccessoryIdx(index)}
                                        className="w-6 h-6 rounded-full grid place-items-center transition-transform hover:scale-110"
                                        title={acc.name}
                                        aria-label={`Afficher ${acc.name}`}
                                    >
                                        <span aria-hidden="true" className={`w-3.5 h-3.5 rounded-full border transition-all ${
                                            activeAccessoryIdx === index
                                                ? 'bg-blue-600 border-blue-600 scale-110 shadow-sm'
                                                : 'bg-slate-200 border-slate-300'
                                        }`} />
                                    </button>
                                ))}
                            </div>

                            {/* Additional Premium Products to fill excess white space */}
                            {activeProductsList.length > 1 && (
                                <div className="mt-4 pt-4 border-t border-slate-100">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-blue-700 mb-3 flex items-center gap-1.5">
                                        <Sparkles className="w-3.5 h-3.5 text-amber-500 animate-pulse" />
                                        Offres Spéciales Recommandées
                                    </h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {activeProductsList
                                            .filter((_, idx) => idx !== activeAccessoryIdx)
                                            .slice(0, 2)
                                            .map((item, idx) => (
                                                <div 
                                                    key={`crystal-item-${item.id || idx}-${idx}`} 
                                                    className="group/mini p-3 bg-slate-50/50 hover:bg-white border border-slate-100 hover:border-blue-200 hover:shadow-sm rounded-2xl transition-all duration-300 flex flex-col justify-between text-left"
                                                >
                                                    <div 
                                                        className="relative h-20 w-full mb-2 cursor-pointer bg-white rounded-xl border border-slate-100/50 flex items-center justify-center p-1.5 overflow-hidden"
                                                        onClick={() => handleProductClick(item)}
                                                    >
                                                        <ModelImage 
                                                            id={String(item.id)}
                                                            type={item.dbItem?.catalogKind === 'PHONE' ? 'product' : 'part'}
                                                            src={item.image}
                                                            alt={item.name}
                                                            className="w-full h-full object-contain group-hover/mini:scale-105 transition-transform duration-300"
                                                            noPadding={true}
                                                            optimizeWidth={160}
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <h5 
                                                            className="text-[10px] font-black text-slate-800 line-clamp-1 hover:text-blue-600 cursor-pointer uppercase tracking-tight"
                                                            onClick={() => handleProductClick(item)}
                                                            title={item.name}
                                                        >
                                                            {item.name}
                                                        </h5>
                                                        <div className="flex items-center justify-between gap-1 mt-1">
                                                            <span className="text-[10px] font-black text-blue-600">{item.price?.toFixed(2)} CHF</span>
                                                            <button 
                                                                onClick={() => handleAddAccessoryToCart(item)}
                                                                className="text-[8px] font-black uppercase tracking-wider text-white bg-slate-900 hover:bg-blue-600 px-2 py-1 rounded-lg transition-colors active:scale-95"
                                                            >
                                                                + Ajouter
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Accessory CTA redirect */}
                        <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
                            <div>
                                <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">Accessoires universels</h4>
                                <p className="text-[10px] text-slate-500 font-medium">Bénéficiez du service Premium complet</p>
                            </div>
                            <button
                                onClick={() => {
                                    setPage(Page.Accessories);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-[#2563eb] hover:translate-x-1.5 transition-transform"
                            >
                                Voir Boutique Complexe <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {/* -------------------------------------------------------------
                SECTION: BRANDS GRID PREVIEW (THE TRADITIONAL CAROUSEL ACCORDION)
               ------------------------------------------------------------- */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 relative z-10">
                <div className="bg-white/80 p-8 md:p-12 rounded-[3.5rem] border border-blue-50 shadow-[0_30px_60px_rgba(59,130,246,0.03)] backdrop-blur-md">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 block mb-2">Ingénierie & Compatibilité</span>
                            <h2 className="text-2xl md:text-3xl font-black text-slate-950 uppercase tracking-tight">
                                Sélectionnez Votre Marque fétiche
                            </h2>
                        </div>
                        <p className="text-slate-500 font-medium text-xs md:text-sm max-w-sm leading-normal">
                            Retrouvez les prestations disponibles selon la marque et le modèle de votre smartphone.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {repairData.map((brand) => (
                            <motion.button
                                key={brand.id}
                                whileHover={{ y: -6, scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleSelectBrand(brand.id)}
                                className="group relative bg-[#F8FAFC] p-6 rounded-[2.2rem] border border-blue-100/30 hover:bg-white hover:border-blue-400/60 transition-all text-center flex flex-col items-center gap-4 shadow-sm hover:shadow-xl"
                            >
                                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm group-hover:shadow-md transition-shadow">
                                    <BrandLogo id={brand.id} name={brand.name} logoUrl={brand.logo} className="w-8 h-8 object-contain text-slate-900 dark:text-slate-900 group-hover:text-blue-600 transition-all duration-300" />
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-xs font-black text-slate-800 group-hover:text-blue-600 transition-colors uppercase tracking-tight">
                                        {brand.name}
                                    </h3>
                                    <span className="text-[9px] font-bold text-slate-600 uppercase tracking-wider">
                                        {(brand.models || []).length} Modèles
                                    </span>
                                </div>
                                <div className="w-8 h-8 rounded-full bg-blue-50 group-hover:bg-blue-600 text-blue-600 group-hover:text-white flex items-center justify-center transition-colors shadow-sm">
                                    <ChevronRight className="w-4 h-4" />
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </div>
            </div>

            {/* -------------------------------------------------------------
                SECTION: REASSURANCES MATRIX (EMOTIONAL DRIVER FOR HIGH CONVERSION)
               ------------------------------------------------------------- */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    
                    <div className="bg-white/80 p-8 rounded-[2.5rem] border border-blue-50 flex gap-4 items-start shadow-sm">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50/50 flex flex-shrink-0 items-center justify-center text-blue-600">
                            <ShieldCheck className="w-6 h-6" />
                        </div>
                        <div className="space-y-1.5 text-left">
                            <h4 className="text-xs font-black uppercase text-slate-900 tracking-tight">Composants adaptés</h4>
                            <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                                Le composant proposé est précisé selon l'appareil et l'option convenue.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white/80 p-8 rounded-[2.5rem] border border-blue-50 flex gap-4 items-start shadow-sm">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50/50 flex flex-shrink-0 items-center justify-center text-blue-600">
                            <Zap className="w-6 h-6" />
                        </div>
                        <div className="space-y-1.5 text-left">
                            <h4 className="text-xs font-black uppercase text-slate-900 tracking-tight font-sans">Diagnostic en atelier</h4>
                            <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                                Le diagnostic permet de confirmer la prestation, la pièce et le délai applicables.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white/80 p-8 rounded-[2.5rem] border border-blue-50 flex gap-4 items-start shadow-sm">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50/50 flex flex-shrink-0 items-center justify-center text-blue-600">
                            <Star className="w-6 h-6" />
                        </div>
                        <div className="space-y-1.5 text-left">
                            <h4 className="text-xs font-black uppercase text-slate-900 tracking-tight">Accompagnement client</h4>
                            <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                                Un accompagnement disponible par téléphone et WhatsApp pour identifier la prestation adaptée.
                            </p>
                        </div>
                    </div>

                </div>
            </div>

            {/* Google Reviews & Interactive Site Map Section */}
            <div id="reviews-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16 relative z-20" style={{ contentVisibility: 'auto', containIntrinsicSize: '900px' }}>
                
                {/* Google Reviews Subsection */}
                <div className="space-y-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 text-left">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
                                <span className="text-[10px] font-black uppercase text-amber-600 tracking-widest">Avis clients publiés</span>
                            </div>
                            <h3 className="text-2xl md:text-4xl font-black uppercase tracking-tight text-slate-900">
                                Ce que nos clients disent de nous
                            </h3>
                            <p className="text-xs md:text-sm text-slate-500 max-w-xl font-medium leading-relaxed">
                                La satisfaction client est au cœur de notre métier. Découvrez les avis authentiques de notre communauté d'utilisateurs.
                            </p>
                        </div>

                        {/* Average Google Rating Score Badge */}
                        <div className="bg-slate-50 border border-slate-100 p-4 rounded-[2rem] flex items-center gap-4 shadow-sm shrink-0">
                            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-700 font-black text-xl">
                                {context?.footerInfo?.google_rating || "4.9"}
                            </div>
                            <div className="text-left">
                                <div className="flex gap-0.5">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} size={13} className="text-amber-500 fill-amber-500" />
                                    ))}
                                </div>
                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">
                                    {context?.footerInfo?.google_reviews_count || 142} AVIS CERTIFIÉS GOOGLE
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Horizontal scrollable or multi-column grid of reviews */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {mergedReviews.slice(0, 6).map((review, rIdx) => (
                            <motion.div 
                                key={`review-${review.id || 'fallback'}-${rIdx}`} 
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: rIdx * 0.1 }}
                                whileHover={{ scale: 1.02, y: -5 }}
                                className="bg-white border border-slate-100 hover:border-blue-200 hover:shadow-lg rounded-[2rem] p-6 text-left space-y-4 transition-all duration-300 flex flex-col justify-between cursor-pointer"
                            >
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            {review.author_photo_url ? (
                                                <img 
                                                    src={reviewAvatarImage(review.author_photo_url, 80)}
                                                    alt={review.customer_name || "Client"} 
                                                    className="w-10 h-10 rounded-full object-cover border border-slate-100" 
                                                    referrerPolicy="no-referrer"
                                                    width={40}
                                                    height={40}
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 font-bold text-sm flex items-center justify-center uppercase">
                                                    {(review.customer_name || "Client").charAt(0)}
                                                </div>
                                            )}
                                            <div>
                                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-tight">{review.customer_name || "Client"}</h4>
                                                <span className="text-[9px] text-slate-400 font-semibold">{review.relative_time_description || "Il y a quelques jours"}</span>
                                            </div>
                                        </div>

                                        <span className="text-[9px] bg-blue-50 text-blue-600 font-black tracking-widest uppercase px-2 py-0.5 rounded-full border border-blue-100/50">
                                            {review.source || "Google"}
                                        </span>
                                    </div>

                                    {/* Star Rating */}
                                    <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, i) => (
                                            <Star 
                                                key={i} 
                                                size={11} 
                                                className={i < review.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"} 
                                            />
                                        ))}
                                    </div>

                                    {/* Text */}
                                    <p className="text-xs text-slate-600 leading-relaxed font-medium italic">
                                        "{review.comment}"
                                    </p>
                                </div>

                                {review.is_verified === 1 && (
                                    <div className="pt-3 border-t border-slate-50 flex items-center gap-1 text-[9px] font-black text-emerald-600 uppercase tracking-wider">
                                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                                        <span>Achat avec informations vérifiées</span>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>

                    {/* Google reviews action button. Its URL is projected from the governed Google Place ID. */}
                    {context?.footerInfo?.google_review_url && <div className="flex justify-center">
                        <a 
                            href={context.footerInfo.google_review_url}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="bg-slate-900 hover:bg-blue-600 text-white hover:scale-[1.02] active:scale-[0.98] transition-all px-8 py-3.5 rounded-2xl font-black uppercase tracking-wider text-xs flex items-center gap-2 shadow-lg shadow-slate-950/10 hover:shadow-blue-600/10"
                        >
                            <span>Laisser un avis sur Google</span>
                            <ExternalLink size={14} />
                        </a>
                    </div>}
                </div>

                {/* Interactive Site Map and Location Info Subsection */}
                <div className="bg-[#0B1120] rounded-[3rem] border border-slate-800 p-8 sm:p-12 text-white relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl -z-10 pointer-events-none" />
                    
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        
                        {/* Info details column */}
                        <div className="lg:col-span-5 space-y-8 text-left">
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5 text-blue-400 animate-bounce" />
                                    <span className="text-[10px] font-black uppercase text-blue-400 tracking-widest">Localisation de notre Boutique</span>
                                </div>
                                <h3 className="text-xl sm:text-3xl font-black uppercase tracking-tight">
                                    Rendez-nous visite à l'Atelier
                                </h3>
                                <p className="text-xs text-slate-400 leading-relaxed font-medium">
                                    Besoin d'un diagnostic direct, d'une réparation adaptée ou simplement envie d'essayer nos derniers accessoires ? Passez nous voir directement, notre équipe est là pour vous accueillir.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex gap-4 items-start">
                                    <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-blue-400 shrink-0 mt-0.5">
                                        <MapPin className="w-5 h-5" />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Notre Adresse</div>
                                        <p className="text-xs text-white font-bold whitespace-pre-line leading-relaxed">
                                            {context?.footerInfo?.adresse}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4 items-start">
                                    <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-blue-400 shrink-0 mt-0.5">
                                        <Clock className="w-5 h-5" />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Heures d'ouverture</div>
                                        <p className="text-xs text-white font-bold leading-relaxed">
                                             {context?.footerInfo?.heures_ouverture}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4 items-start">
                                    <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-blue-400 shrink-0 mt-0.5">
                                        <Phone className="w-5 h-5" />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Téléphone</div>
                                        {context?.footerInfo?.telephone ? (
                                            <a href={`tel:${context.footerInfo.telephone}`} className="text-xs text-white hover:text-blue-400 transition-colors font-black leading-relaxed">
                                                {context.footerInfo.telephone}
                                            </a>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="flex gap-4 items-start">
                                    <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-blue-400 shrink-0 mt-0.5">
                                        <Mail className="w-5 h-5" />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">E-mail</div>
                                        {context?.footerInfo?.email ? (
                                            <a href={`mailto:${context.footerInfo.email}`} className="text-xs text-white hover:text-blue-400 transition-colors font-black leading-relaxed">
                                                {context.footerInfo.email}
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Interactive Google Map iframe column */}
                        {context?.footerInfo?.adresse ? <div className="lg:col-span-7 h-80 sm:h-96 w-full rounded-[2.5rem] overflow-hidden border border-slate-800 bg-slate-950 p-2 shadow-inner">
                            <iframe 
                                title={`Plan d'accès ${context?.footerInfo?.siteName || context?.footerInfo?.nom_du_magasin || 'Mobilbo'}`}
                                src={`https://maps.google.com/maps?q=${encodeURIComponent(context.footerInfo.adresse)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                className="w-full h-full rounded-[2.2rem] border-0 outline-none"
                                allowFullScreen
                                loading="lazy"
                            />
                        </div> : null}

                    </div>
                </div>

            </div>

            {/* Immersive Mobile-Optimized video showcase Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/90 backdrop-blur-lg p-4 sm:p-6"
                    >
                        <motion.div 
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 350 }}
                            className="bg-[#0B1120] border border-slate-800 rounded-[2rem] sm:rounded-[2.5rem] w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col relative"
                        >
                            {/* Close Button top-right */}
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsMOpen(false);
                                }}
                                className="absolute top-4 right-4 z-50 bg-black/60 hover:bg-black/90 p-2.5 rounded-full text-slate-400 hover:text-white transition-all backdrop-blur-md border border-white/10"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            {/* Video Display Container */}
                            <div className="relative aspect-video w-full bg-black flex items-center justify-center overflow-hidden" style={hasCustomVideo ? {} : { backgroundImage: `url(${fallbackPoster})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                                {hasCustomVideo ? (
                                    <video 
                                        ref={modalVideoRef}
                                        src={activeVideoUrl}
                                        className="w-full h-full object-contain backdrop-blur-md bg-black/40"
                                        autoPlay
                                        loop
                                        muted={isMuted}
                                        playsInline
                                        webkit-playsinline="true"
                                        preload="auto"
                                        poster={fallbackPoster}
                                    />
                                ) : (
                                    fallbackPoster ? <img src={fallbackPoster} alt={`Vitrine ${context?.footerInfo?.siteName || context?.footerInfo?.nom_du_magasin || 'Mobilbo'}`} className="w-full h-full object-cover" width="1920" height="1080" /> : <div className="w-full h-full bg-slate-950" aria-hidden="true" />
                                )}
                                
                                {/* Overlay control buttons */}
                                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center z-10">
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsMuted(!isMuted);
                                        }}
                                        className="bg-black/70 hover:bg-black/90 text-slate-200 hover:text-white px-4 py-2 rounded-full text-xs font-bold tracking-wider flex items-center gap-2 border border-white/5 transition-all"
                                    >
                                        {isMuted ? (
                                            <>
                                                <VolumeX className="w-4 h-4 text-rose-400" />
                                                <span>Activer le Son</span>
                                            </>
                                        ) : (
                                            <>
                                                <Volume2 className="w-4 h-4 text-emerald-400" />
                                                <span>Couper le Son</span>
                                            </>
                                        )}
                                    </button>

                                    <span className="bg-blue-600/95 text-white px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg shadow-blue-500/20">
                                        Live Atelier
                                    </span>
                                </div>
                            </div>

                            {/* Details Text Content inside Modal */}
                            <div className="p-6 sm:p-8 space-y-4 text-left">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-blue-500" />
                                    <span className="text-[10px] font-black uppercase text-blue-400 tracking-widest">Savoir-faire {(context?.footerInfo?.siteName || context?.footerInfo?.nom_du_magasin || 'Mobilbo')}</span>
                                </div>
                                <h3 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">Atelier de Restauration de {getCityFromAddress(context?.footerInfo?.adresse)}</h3>
                                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-xl text-left">
                                    Les appareils sont contrôlés selon l'intervention réalisée. De la micro-soudure sur carte mère au remplacement d'écran ou de batterie, l'équipe précise les options disponibles avant la prise en charge.
                                </p>
                                
                                <div className="pt-2 flex flex-wrap gap-2.5">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-xl">⚡ {publicClaimContract.home.diagnosticBadge}</span>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-xl">🛠️ Équipements de pointe</span>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-xl">🛡️ Composants adaptés</span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className={getThemeLayoutClass(context?.activeThemeConfig, 'crystalDiscovery')}>
                <TopicClusters />
            </div>
        </div>
    );
};

export default CrystalTheme;
